import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { queryKeys } from '@/adapter/opm-cockpit-backend/queryKeys';
import { useApiClient } from '@/adapter/opm-cockpit-backend/useApiClient';
import { type VorgangDetailResponse } from '@/adapter/opm-cockpit-backend/useVorgangApi';
import { useAuth } from '@/features/auth/AuthProvider';
import { VorgangProzessSchritt } from '@/features/schemaValidation/stepValidationSchemas';
import { deleteValueAtPath, findFehlerPayload, setValueAtPath } from '../types';

interface MassSavePatch {
  path: string;
  value: unknown;
}

interface MassSaveResult {
  success: string[];
  failed: { id: string; error: string }[];
}

interface MassSaveParams {
  vorgangIds: string[];
  patches: readonly MassSavePatch[];
  deletes?: readonly string[];
  suppressSuccessToast?: boolean;
}

interface UseMassSavePfadWerteResult {
  save: (params: MassSaveParams) => Promise<MassSaveResult | null>;
  isSaving: boolean;
}

export const useMassSavePfadWerte = (
  schritt: VorgangProzessSchritt,
): UseMassSavePfadWerteResult => {
  const { vorgaengeApi, axiosInstance } = useApiClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const initiator = user?.email ?? '';
  const [isSaving, setIsSaving] = useState(false);

  const save = async ({
    vorgangIds,
    patches,
    deletes = [],
    suppressSuccessToast = false,
  }: MassSaveParams): Promise<MassSaveResult | null> => {
    if (!initiator) {
      toast.error('Nicht angemeldet — Speichern nicht möglich.');
      return null;
    }
    setIsSaving(true);

    const results = await Promise.allSettled(
      vorgangIds.map(async (id) => {
        const response = await axiosInstance.get<VorgangDetailResponse>(
          `/vorgaenge/${id}`,
        );
        const fehlerSchrittPayload = findFehlerPayload(response.data, schritt);
        if (fehlerSchrittPayload === null) {
          throw new Error(`Kein ${schritt}-Schritt in Vorgang ${id}`);
        }
        let newPayload: unknown = fehlerSchrittPayload;
        for (const { path, value } of patches) {
          newPayload = setValueAtPath(newPayload, path, value);
        }
        for (const path of deletes) {
          newPayload = deleteValueAtPath(newPayload, path);
        }
        await vorgaengeApi.saveEvent({
          id,
          saveEventRequest: {
            eventData: JSON.stringify(newPayload),
            initiator,
          },
        });
      }),
    );

    const success = results.flatMap((r, i) =>
      r.status === 'fulfilled' ? [vorgangIds[i]] : [],
    );
    const failed = results.flatMap((r, i) => {
      if (r.status !== 'rejected') return [];
      const reason = r.reason;
      const message = reason instanceof Error ? reason.message : String(reason);
      return [{ id: vorgangIds[i], error: message }];
    });

    const vorgangWort = (n: number) => (n === 1 ? 'Vorgang' : 'Vorgänge');

    if (failed.length === 0) {
      if (!suppressSuccessToast) {
        toast.success(
          `${success.length} ${vorgangWort(success.length)} gespeichert`,
        );
      }
    } else if (success.length === 0) {
      toast.error(
        `Speichern fehlgeschlagen für ${failed.length} ${vorgangWort(failed.length)}`,
      );
    } else {
      toast.warning(`${success.length} OK, ${failed.length} fehlgeschlagen`);
    }
    if (failed.length > 0) {
      // eslint-disable-next-line no-console
      console.error('[Massenverarbeitung] Bulk-Save Fehler', failed);
    }

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.vorgaenge.lists(),
      }),
      ...success.map((id) =>
        queryClient.invalidateQueries({
          queryKey: queryKeys.vorgaenge.detail(id),
        }),
      ),
    ]);

    setIsSaving(false);

    return { success, failed };
  };

  return { save, isSaving };
};
